/**
 * Toast notification component with accessibility and animations
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './Toast.css';

const Toast = ({
  id,
  type = 'info',
  title,
  message,
  duration = 5000,
  persistent = false,
  onClose,
  action,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Show toast with slight delay for animation
    const showTimer = setTimeout(() => setIsVisible(true), 100);

    // Auto-dismiss timer
    let dismissTimer;
    if (!persistent && duration > 0) {
      dismissTimer = setTimeout(() => {
        handleClose();
      }, duration);
    }

    return () => {
      clearTimeout(showTimer);
      if (dismissTimer) {
        clearTimeout(dismissTimer);
      }
    };
  }, [duration, persistent]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose?.(id);
    }, 300); // Match CSS animation duration
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
            <polyline points='20,6 9,17 4,12' />
          </svg>
        );
      case 'error':
        return (
          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
            <circle cx='12' cy='12' r='10' />
            <line x1='15' y1='9' x2='9' y2='15' />
            <line x1='9' y1='9' x2='15' y2='15' />
          </svg>
        );
      case 'warning':
        return (
          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
            <path d='m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' />
            <line x1='12' y1='9' x2='12' y2='13' />
            <line x1='12' y1='17' x2='12.01' y2='17' />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
            <circle cx='12' cy='12' r='10' />
            <line x1='12' y1='16' x2='12' y2='12' />
            <line x1='12' y1='8' x2='12.01' y2='8' />
          </svg>
        );
    }
  };

  const toastClasses = [
    'toast',
    `toast--${type}`,
    isVisible && 'toast--visible',
    isLeaving && 'toast--leaving',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={toastClasses} role='alert' aria-live='polite' aria-atomic='true'>
      <div className='toast__icon' aria-hidden='true'>
        {getIcon()}
      </div>

      <div className='toast__content'>
        {title && <div className='toast__title'>{title}</div>}
        <div className='toast__message'>{message}</div>

        {action && (
          <div className='toast__action'>
            <button type='button' className='toast__action-button' onClick={action.onClick}>
              {action.label}
            </button>
          </div>
        )}
      </div>

      {!persistent && (
        <button
          type='button'
          className='toast__close'
          onClick={handleClose}
          aria-label='Close notification'
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>
      )}
    </div>
  );
};

Toast.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['info', 'success', 'warning', 'error']),
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  duration: PropTypes.number,
  persistent: PropTypes.bool,
  onClose: PropTypes.func,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
};

export default Toast;
